import { BoxType, GroupPeopleType, type CustomGroupTemplateBox, type CustomTemplateBox } from './custom-template';
import { referencedBoxIds } from './box-references';

/**
 * Generation reads a flat layer list: a plain Pass Through PSD folder node (ADR 0043) dissolves into its children,
 * in order. Kept: groups without `psdGroup` (roster cells, owner cards), an isolated, styled, masked, hidden, locked,
 * spread-wide or person-typed node, and a clip / mask base other boxes point at.
 */
export function flattenPsdGroups(boxes: readonly CustomTemplateBox[]): CustomTemplateBox[] {
  const dissolves = psdFolderDissolves(boxes);
  const walk = (list: readonly CustomTemplateBox[]): CustomTemplateBox[] =>
    list.flatMap(box => {
      if (box.type !== BoxType.GROUP) return [box];
      const inner = walk(box.boxes);
      return dissolves(box) ? inner : [{ ...box, boxes: inner }];
    });

  return walk(boxes);
}

/** The dissolve verdict of `flattenPsdGroups` for a group of `boxes` (the whole spread, references are spread-wide). */
export function psdFolderDissolves(boxes: readonly CustomTemplateBox[]): (group: CustomGroupTemplateBox) => boolean {
  const referenced = referencedBoxIds(boxes);
  return group => plainPsdGroup(group) && !referenced.has(group.id);
}

const set = (v: unknown): boolean => v !== undefined && v !== null && v !== false;

function plainPsdGroup(g: CustomGroupTemplateBox): boolean {
  const { psdGroup, personId, peopleType, ...rest } = g.params;
  if (!psdGroup || psdGroup.isolated) return false;
  if (Object.entries(psdGroup).some(([k, v]) => k !== 'v' && k !== 'isolated' && set(v))) return false;
  if (set(personId) || (peopleType && peopleType !== GroupPeopleType.FREE) || Object.values(rest).some(set)) return false;
  // ponytail: children are group-relative, only a node at the document origin dissolves without a shift; a folder
  // the designer moved stays a node until the box shift (template-document `shiftTemplateBox`) is shared.
  if (g.rect[0] !== 0 || g.rect[1] !== 0 || (g.rotation ?? 0) !== 0) return false;
  if (g.visible === false || g.skipRender || g.locked || g.spreadWide || g.flipHorizontal || g.flipVertical) return false;
  if ((g.opacity ?? 1) !== 1 || (g.fillOpacity ?? 1) !== 1) return false;
  if (g.blendMode && g.blendMode !== 'pass through') return false;
  return ![g.effects, g.layerStyleRendering, g.layerStyleMasks, g.blendIf, g.knockout, g.clipping].some(set);
}
